import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    NumberInput,
    ScrollArea,
    Stack,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    SubscriptionItemsFilters,
    useSubscriptionItemsFilter,
} from '../hooks/useSubscriptionItemsFilter';

/**
 * FNP-918: the subscription-items filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useSubscriptionItemsFilter), not component state, so the
 * table — a sibling, not a child — filters the same list.
 *
 * ⚠️ Only `quantity` is offered, and this module has the THINNEST filter
 * surface of the four. A SubscriptionItem carries just id, price, quantity,
 * subscription and created: `id` and `price` are free text already covered by
 * the keyword search, `subscription` is fixed by the scope, and `created` is a
 * timestamp better served by sorting the column. Quantity is the one field an
 * operator picks a discrete value from. Adding controls for the rest would be
 * building for a hypothetical use case.
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — the server filter is the
 * subscription scope and nothing more. See useSubscriptionItems.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useSubscriptionItemsFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Subscription Items')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<SubscriptionItemsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <NumberInput
                                label={I18n.get('Quantity')}
                                placeholder={I18n.get('Any')}
                                min={0}
                                // Filters are compared as strings in
                                // useSubscriptionItems, so the value is stored
                                // as one. An empty field means "unset" and is
                                // ignored rather than matching nothing.
                                value={values.quantity === '' || values.quantity === undefined
                                    ? ''
                                    : Number(values.quantity)}
                                onChange={(value) =>
                                    setFieldValue(
                                        'quantity',
                                        value === '' || value === null
                                            ? ''
                                            : String(value),
                                    )}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
